import React, { useEffect, useState, memo } from 'react';
import { ChevronDownIcon, CheckIcon, StarIcon, BookOpenIcon, ClockIcon, UsersIcon, XIcon } from 'lucide-react';
export function LandingPage() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  useEffect(() => {
    setIsVisible(true);
  }, []);
  return <main className="w-full min-h-screen bg-parchment" data-id="element-2">
      {/* Hero Section */}
      <section className="relative w-full min-h-screen flex items-center justify-center px-4 py-16 overflow-hidden" data-id="element-3">
        <div className="absolute inset-0 opacity-5" data-id="element-4">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #8B1A1A 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }} data-id="element-5" />
        </div>

        <div className={`relative max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-6">
          <div className="space-y-6" data-id="element-7">
            <div className="inline-block px-4 py-2 bg-burgundy/10 border border-burgundy/20 rounded-full" data-id="element-8">
              <span className="text-burgundy font-lora text-sm font-medium" data-id="element-9">
                Receta Familiar Auténtica
              </span>
            </div>

            <h1 className="font-playfair text-5xl md:text-6xl lg:text-7xl font-bold text-brown leading-tight" data-id="element-10">
              Empanadas de la Abuela
            </h1>

            <p className="font-playfair italic text-2xl md:text-3xl text-burgundy" data-id="element-11">
              Receta Secreta Paso a Paso
            </p>

            <p className="font-lora text-lg md:text-xl text-brown/80 leading-relaxed" data-id="element-12">
              ¿Por qué las empanadas de la abuela siempre sabían mejor? No eran
              solo los ingredientes...
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-4" data-id="element-13">
              <a href="https://saboresdetradicion.com/cart/44001890107527:1" className="block w-full bg-burgundy text-cream font-lora font-bold text-xl py-5 rounded-lg shadow-lg hover:bg-burgundy/90 hover:shadow-xl transition-all duration-300 hover:scale-105 mb-4 text-center" data-id="element-14">
                Obtener la Receta Ahora
              </a>
              <span className="inline-flex items-center justify-center px-8 py-4 bg-cream text-burgundy font-lora font-semibold text-lg rounded-lg border-2 border-burgundy/20" data-id="element-15">
                Descubre el Secreto
              </span>
            </div>

            <div className="flex items-center gap-6 pt-6 border-t border-brown/10" data-id="element-16">
              <div className="flex items-center gap-2" data-id="element-17">
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-18" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-19" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-20" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-21" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-22" />
              </div>
              <span className="font-lora text-sm text-brown/70" data-id="element-23">
                Más de 2,500 familias satisfechas
              </span>
            </div>
          </div>

          <div className="relative" data-id="element-24">
            <div className="absolute -inset-4 bg-gradient-to-br from-burgundy/20 to-gold/20 rounded-3xl blur-2xl" data-id="element-25" />
            <img src="https://cdn.magicpatterns.com/uploads/npCeNXeoULfMeVqsGV9wSu/receta_abuela.png" alt="Empanadas de la Abuela - Receta Secreta" className="relative w-full rounded-2xl shadow-2xl border-8 border-cream" data-id="element-26" />
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce" data-id="element-27">
          <ChevronDownIcon className="w-8 h-8 text-burgundy/40" data-id="element-28" />
        </div>
      </section>

      {/* Story Section */}
      <section id="historia" className="w-full py-20 px-4 bg-cream" data-id="element-29">
        <div className="max-w-4xl mx-auto" data-id="element-30">
          <div className="bg-parchment rounded-2xl shadow-xl p-8 md:p-12 border-4 border-brown/10 relative" data-id="element-31">
            <div className="absolute -top-6 left-8 bg-burgundy text-cream px-6 py-2 rounded-full shadow-lg" data-id="element-32">
              <span className="font-playfair italic text-lg" data-id="element-33">La Historia</span>
            </div>

            <div className="space-y-6 font-lora text-lg leading-relaxed text-brown/90" data-id="element-34">
              <p className="text-xl md:text-2xl font-playfair italic text-burgundy leading-relaxed" data-id="element-35">
                "Hay un aroma que no se olvida."
              </p>

              <p data-id="element-36">
                Es ese olor a cebolla rehogada, carne de calidad y masa caliente
                que inundaba la casa los domingos por la mañana. Te acercabas a
                la cocina y ahí estaba ella: con las manos enharinadas, cerrando
                cada empanada con un movimiento que parecía magia.
              </p>

              <p data-id="element-37">
                Intentaste replicarlo. Compraste la mejor carne, miraste videos
                en YouTube... pero no es lo mismo. Algo falta.
              </p>

              <p className="text-xl font-medium text-burgundy" data-id="element-38">
                Te quedan secas, o la masa se rompe, o simplemente les falta{' '}
                <span className="font-playfair italic" data-id="element-39">'ese'</span> alma.
              </p>

              <div className="pt-6 border-t-2 border-burgundy/20" data-id="element-40">
                <p className="text-xl font-medium text-brown" data-id="element-41">
                  La mayoría de las recetas modernas olvidan lo más importante:{' '}
                  <span className="font-playfair italic text-burgundy" data-id="element-42">
                    la empanada no es comida rápida. Es un proceso de respeto.
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="w-full py-20 px-4" data-id="element-43">
        <div className="max-w-6xl mx-auto" data-id="element-44">
          <div className="text-center mb-16" data-id="element-45">
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-brown mb-4" data-id="element-46">
              El Secreto del Relleno Jugoso
            </h2>
            <p className="font-playfair italic text-2xl text-burgundy" data-id="element-47">
              Que las rotiserías no quieren que sepas
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-48">
            <div className="bg-cream rounded-xl p-8 shadow-lg border-2 border-brown/10 hover:shadow-xl transition-shadow duration-300" data-id="element-49">
              <div className="w-16 h-16 bg-burgundy/10 rounded-full flex items-center justify-center mb-6" data-id="element-50">
                <BookOpenIcon className="w-8 h-8 text-burgundy" data-id="element-51" />
              </div>
              <h3 className="font-playfair text-2xl font-bold text-brown mb-4" data-id="element-52">
                Ritual Decodificado
              </h3>
              <p className="font-lora text-brown/80 leading-relaxed" data-id="element-53">
                No solo cocinar, sino elegir la carne, tratar la grasa, dejar
                que el relleno 'hable' con el tiempo exacto de reposo.
              </p>
            </div>

            <div className="bg-cream rounded-xl p-8 shadow-lg border-2 border-brown/10 hover:shadow-xl transition-shadow duration-300" data-id="element-54">
              <div className="w-16 h-16 bg-burgundy/10 rounded-full flex items-center justify-center mb-6" data-id="element-55">
                <ClockIcon className="w-8 h-8 text-burgundy" data-id="element-56" />
              </div>
              <h3 className="font-playfair text-2xl font-bold text-brown mb-4" data-id="element-57">
                Tiempos Precisos
              </h3>
              <p className="font-lora text-brown/80 leading-relaxed" data-id="element-58">
                Cada paso tiene su momento. Aprende cuándo rehogar, cuándo
                reposar, cuándo cerrar. El timing es todo.
              </p>
            </div>

            <div className="bg-cream rounded-xl p-8 shadow-lg border-2 border-brown/10 hover:shadow-xl transition-shadow duration-300" data-id="element-59">
              <div className="w-16 h-16 bg-burgundy/10 rounded-full flex items-center justify-center mb-6" data-id="element-60">
                <UsersIcon className="w-8 h-8 text-burgundy" data-id="element-61" />
              </div>
              <h3 className="font-playfair text-2xl font-bold text-brown mb-4" data-id="element-62">
                Tu Legado Familiar
              </h3>
              <p className="font-lora text-brown/80 leading-relaxed" data-id="element-63">
                Conviértete en el mejor cocinero de la familia. Crea momentos
                que tus hijos recordarán toda la vida.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="w-full py-20 px-4 bg-gradient-to-b from-parchment to-cream" data-id="element-64">
        <div className="max-w-5xl mx-auto" data-id="element-65">
          <div className="text-center mb-16" data-id="element-66">
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-brown mb-4" data-id="element-67">
              Lo Que Aprenderás
            </h2>
            <p className="font-lora text-xl text-brown/70" data-id="element-68">
              Cada secreto, cada técnica, cada detalle que marca la diferencia
            </p>
          </div>

          <div className="space-y-6" data-id="element-69">
            <div className="flex items-start gap-4 bg-parchment rounded-xl p-6 shadow-md border border-brown/10" data-id="element-70">
              <div className="flex-shrink-0 w-8 h-8 bg-burgundy rounded-full flex items-center justify-center" data-id="element-71">
                <CheckIcon className="w-5 h-5 text-cream" data-id="element-72" />
              </div>
              <div data-id="element-73">
                <h3 className="font-playfair text-xl font-bold text-brown mb-2" data-id="element-74">
                  La Selección de la Carne
                </h3>
                <p className="font-lora text-brown/80" data-id="element-75">
                  Qué corte usar, cómo pedirlo al carnicero, y por qué la
                  proporción de grasa es crítica para el sabor.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-parchment rounded-xl p-6 shadow-md border border-brown/10" data-id="element-76">
              <div className="flex-shrink-0 w-8 h-8 bg-burgundy rounded-full flex items-center justify-center" data-id="element-77">
                <CheckIcon className="w-5 h-5 text-cream" data-id="element-78" />
              </div>
              <div data-id="element-79">
                <h3 className="font-playfair text-xl font-bold text-brown mb-2" data-id="element-80">
                  El Secreto para que la cebolla quede en su punto
                </h3>
                <p className="font-lora text-brown/80" data-id="element-81">
                  Aprende la técnica exacta de cocción para que la cebolla sude
                  y caramelice en el punto justo, liberando sus azúcares
                  naturales y creando una base de sabor profunda, dulce y
                  memorable: la clave del relleno realmente irresistible.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-parchment rounded-xl p-6 shadow-md border border-brown/10" data-id="element-82">
              <div className="flex-shrink-0 w-8 h-8 bg-burgundy rounded-full flex items-center justify-center" data-id="element-83">
                <CheckIcon className="w-5 h-5 text-cream" data-id="element-84" />
              </div>
              <div data-id="element-85">
                <h3 className="font-playfair text-xl font-bold text-brown mb-2" data-id="element-86">
                  La Masa Perfecta
                </h3>
                <p className="font-lora text-brown/80" data-id="element-87">
                  Proporciones exactas, temperatura del agua, tiempo de amasado.
                  Una masa que no se rompe y mantiene el jugo dentro.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-parchment rounded-xl p-6 shadow-md border border-brown/10" data-id="element-88">
              <div className="flex-shrink-0 w-8 h-8 bg-burgundy rounded-full flex items-center justify-center" data-id="element-89">
                <CheckIcon className="w-5 h-5 text-cream" data-id="element-90" />
              </div>
              <div data-id="element-91">
                <h3 className="font-playfair text-xl font-bold text-brown mb-2" data-id="element-92">
                  El Repulgue que No Falla
                </h3>
                <p className="font-lora text-brown/80" data-id="element-93">
                  Paso a paso fotográfico del cierre tradicional. Aprende el
                  movimiento exacto que usaba la abuela.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-parchment rounded-xl p-6 shadow-md border border-brown/10" data-id="element-94">
              <div className="flex-shrink-0 w-8 h-8 bg-burgundy rounded-full flex items-center justify-center" data-id="element-95">
                <CheckIcon className="w-5 h-5 text-cream" data-id="element-96" />
              </div>
              <div data-id="element-97">
                <h3 className="font-playfair text-xl font-bold text-brown mb-2" data-id="element-98">
                  Cocción al Horno
                </h3>
                <p className="font-lora text-brown/80" data-id="element-99">
                  Temperaturas exactas, tiempos precisos, y el truco para que
                  queden doradas y crocantes sin quemarse.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-parchment rounded-xl p-6 shadow-md border border-brown/10" data-id="element-100">
              <div className="flex-shrink-0 w-8 h-8 bg-burgundy rounded-full flex items-center justify-center" data-id="element-101">
                <CheckIcon className="w-5 h-5 text-cream" data-id="element-102" />
              </div>
              <div data-id="element-103">
                <h3 className="font-playfair text-xl font-bold text-brown mb-2" data-id="element-104">
                  🎁 BONUS: 3 Salsas Secretas de la Abuela
                </h3>
                <p className="font-lora text-brown/80" data-id="element-105">
                  Salsa criolla clásica, chimichurri casero y salsa picante
                  suave — las tres salsas que acompañaban cada empanada en la
                  mesa familiar.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="w-full py-20 px-4 bg-cream" data-id="element-106">
        <div className="max-w-6xl mx-auto" data-id="element-107">
          <div className="text-center mb-16" data-id="element-108">
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-brown mb-4" data-id="element-109">
              Lo Que Dicen Nuestras Familias
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-110">
            <div className="bg-parchment rounded-xl p-8 shadow-lg border-2 border-brown/10" data-id="element-111">
              <div className="flex items-center gap-1 mb-4" data-id="element-112">
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-113" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-114" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-115" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-116" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-117" />
              </div>
              <p className="font-lora italic text-brown/90 mb-6 leading-relaxed" data-id="element-118">
                "Lloré cuando probé la primera. Era exactamente como las hacía
                mi mamá. Después de 15 años intentando, finalmente lo logré."
              </p>
              <p className="font-playfair font-semibold text-burgundy" data-id="element-119">
                — María Elena, Buenos Aires
              </p>
            </div>

            <div className="bg-parchment rounded-xl p-8 shadow-lg border-2 border-brown/10" data-id="element-120">
              <div className="flex items-center gap-1 mb-4" data-id="element-121">
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-122" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-123" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-124" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-125" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-126" />
              </div>
              <p className="font-lora italic text-brown/90 mb-6 leading-relaxed" data-id="element-127">
                "Es increíble, me despierto con antojo de desayunar una de estas
                empanadas."
              </p>
              <p className="font-playfair font-semibold text-burgundy" data-id="element-128">
                — Roberto Gómez, Córdoba
              </p>
            </div>

            <div className="bg-parchment rounded-xl p-8 shadow-lg border-2 border-brown/10" data-id="element-129">
              <div className="flex items-center gap-1 mb-4" data-id="element-130">
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-131" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-132" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-133" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-134" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-135" />
              </div>
              <p className="font-lora italic text-brown/90 mb-6 leading-relaxed" data-id="element-136">
                "Ahora mis hijos me piden empanadas cada domingo, y ya es
                nuestro ritual familiar: amasar, charlar y compartirlas recién
                salidas del horno."
              </p>
              <p className="font-playfair font-semibold text-burgundy" data-id="element-137">
                — Carolina Paz, Mendoza
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="comprar" className="w-full py-20 px-4 bg-gradient-to-br from-burgundy to-burgundy/90" data-id="element-138">
        <div className="max-w-4xl mx-auto text-center" data-id="element-139">
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-cream mb-6" data-id="element-140">
            Recupera el Sabor de Tu Infancia
          </h2>
          <p className="font-lora text-xl text-cream/90 mb-8 leading-relaxed" data-id="element-141">
            El paso a paso de la empanada criolla perfecta. Es el fin de las
            empanadas mediocres y el inicio de tu legado como el mejor cocinero
            de la familia.
          </p>

          <div className="bg-cream rounded-2xl p-8 md:p-12 shadow-2xl mb-8" data-id="element-142">
            <div className="flex items-baseline justify-center gap-2 mb-6" data-id="element-143">
              <span className="font-playfair text-5xl md:text-6xl font-bold text-burgundy" data-id="element-144">
                $ 9.900 ARS
              </span>
            </div>

            <div className="space-y-3 mb-8 text-left max-w-md mx-auto" data-id="element-145">
              <div className="flex items-center gap-3" data-id="element-146">
                <CheckIcon className="w-5 h-5 text-burgundy flex-shrink-0" data-id="element-147" />
                <span className="font-lora text-brown" data-id="element-148">
                  Receta completa paso a paso con fotos
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-149">
                <CheckIcon className="w-5 h-5 text-burgundy flex-shrink-0" data-id="element-150" />
                <span className="font-lora text-brown" data-id="element-151">
                  Lista de compras detallada
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-152">
                <CheckIcon className="w-5 h-5 text-burgundy flex-shrink-0" data-id="element-153" />
                <span className="font-lora text-brown" data-id="element-154">
                  Acceso inmediato y de por vida
                </span>
              </div>
            </div>

            <a href="https://saboresdetradicion.com/cart/44001890107527:1" className="block w-full bg-burgundy text-cream font-lora font-bold text-xl py-5 rounded-lg shadow-lg hover:bg-burgundy/90 hover:shadow-xl transition-all duration-300 hover:scale-105 mb-4 text-center" data-id="element-155">
              Obtener la Receta Ahora
            </a>

            <p className="font-lora text-sm text-brown/60" data-id="element-156">
              Garantía de satisfacción de 7 días. Si no te encanta, te
              devolvemos tu dinero.
            </p>
          </div>

          <div className="bg-gold/20 border-2 border-gold rounded-xl p-6 inline-block" data-id="element-157">
            <p className="font-playfair italic text-lg text-cream" data-id="element-158">
              🔥 Oferta especial: luego vuelve a $29.900 ARS
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full py-12 px-4 bg-brown text-cream/80" data-id="element-159">
        <div className="max-w-6xl mx-auto text-center" data-id="element-160">
          <h3 className="font-playfair text-2xl font-bold text-cream mb-4" data-id="element-161">
            Empanadas de la Abuela
          </h3>
          <p className="font-lora mb-6" data-id="element-162">Receta Secreta Paso a Paso</p>
          <div className="border-t border-cream/20 pt-6 space-y-3" data-id="element-163">
            <div className="flex items-center justify-center gap-6" data-id="element-164">
              <button onClick={() => setActiveModal('privacy')} className="font-lora text-sm text-cream/60 hover:text-cream transition-colors duration-200 underline underline-offset-2" data-id="element-165">
                Política de Privacidad
              </button>
              <span className="text-cream/30" data-id="element-166">·</span>
              <button onClick={() => setActiveModal('terms')} className="font-lora text-sm text-cream/60 hover:text-cream transition-colors duration-200 underline underline-offset-2" data-id="element-167">
                Términos y Condiciones
              </button>
            </div>
            <p className="font-lora text-sm" data-id="element-168">
              © 2024 Empanadas de la Abuela. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>

      {/* Privacy Modal */}
      {activeModal === 'privacy' && <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 bg-brown/70 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-169">
          <div className="relative bg-parchment rounded-2xl shadow-2xl border-4 border-brown/10 max-w-2xl w-full max-h-[80vh] overflow-y-auto p-8 md:p-12" onClick={e => e.stopPropagation()} data-id="element-170">
            <button onClick={() => setActiveModal(null)} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-brown/10 hover:bg-brown/20 transition-colors" data-id="element-171">
              <XIcon className="w-4 h-4 text-brown" data-id="element-172" />
            </button>
            <h2 className="font-playfair text-3xl font-bold text-brown mb-6" data-id="element-173">
              Política de Privacidad
            </h2>
            <div className="space-y-4 font-lora text-brown/80 leading-relaxed" data-id="element-174">
              <p data-id="element-175">
                En{' '}
                <span className="font-semibold text-brown" data-id="element-176">
                  Empanadas de la Abuela
                </span>
                , nos comprometemos a proteger tu información personal. Esta
                política describe cómo recopilamos, usamos y protegemos los
                datos que nos proporcionás.
              </p>
              <h3 className="font-playfair text-xl font-bold text-brown mt-6" data-id="element-177">
                Información que recopilamos
              </h3>
              <p data-id="element-178">
                Al realizar una compra, recopilamos únicamente los datos
                necesarios para procesar tu pedido: nombre, dirección de correo
                electrónico e información de pago. No almacenamos datos de
                tarjetas de crédito.
              </p>
              <h3 className="font-playfair text-xl font-bold text-brown mt-6" data-id="element-179">
                Uso de la información
              </h3>
              <p data-id="element-180">
                Utilizamos tu información exclusivamente para entregar el
                producto digital adquirido y, con tu consentimiento, para
                enviarte comunicaciones relacionadas con nuevos productos o
                actualizaciones.
              </p>
              <h3 className="font-playfair text-xl font-bold text-brown mt-6" data-id="element-181">
                Compartir información
              </h3>
              <p data-id="element-182">
                No vendemos, alquilamos ni compartimos tu información personal
                con terceros, salvo cuando sea estrictamente necesario para
                procesar tu pago a través de plataformas de pago seguras.
              </p>
              <h3 className="font-playfair text-xl font-bold text-brown mt-6" data-id="element-183">
                Cookies
              </h3>
              <p data-id="element-184">
                Este sitio puede utilizar cookies técnicas para mejorar tu
                experiencia de navegación. No utilizamos cookies de seguimiento
                de terceros con fines publicitarios.
              </p>
              <h3 className="font-playfair text-xl font-bold text-brown mt-6" data-id="element-185">
                Tus derechos
              </h3>
              <p data-id="element-186">
                Tenés derecho a acceder, rectificar o eliminar tus datos
                personales en cualquier momento. Para ejercer estos derechos,
                podés comunicarte con nosotros a través de los canales
                disponibles en la plataforma de compra.
              </p>
            </div>
          </div>
        </div>}

      {/* Terms Modal */}
      {activeModal === 'terms' && <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 bg-brown/70 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-187">
          <div className="relative bg-parchment rounded-2xl shadow-2xl border-4 border-brown/10 max-w-2xl w-full max-h-[80vh] overflow-y-auto p-8 md:p-12" onClick={e => e.stopPropagation()} data-id="element-188">
            <button onClick={() => setActiveModal(null)} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-brown/10 hover:bg-brown/20 transition-colors" data-id="element-189">
              <XIcon className="w-4 h-4 text-brown" data-id="element-190" />
            </button>
            <h2 className="font-playfair text-3xl font-bold text-brown mb-6" data-id="element-191">
              Términos y Condiciones
            </h2>
            <div className="space-y-4 font-lora text-brown/80 leading-relaxed" data-id="element-192">
              <p data-id="element-193">
                Al adquirir{' '}
                <span className="font-semibold text-brown" data-id="element-194">
                  Empanadas de la Abuela – Receta Secreta Paso a Paso
                </span>
                , aceptás los siguientes términos y condiciones.
              </p>
              <h3 className="font-playfair text-xl font-bold text-brown mt-6" data-id="element-195">
                Producto digital
              </h3>
              <p data-id="element-196">
                Este es un producto digital de descarga inmediata. Una vez
                completada la compra, recibirás acceso permanente al material
                adquirido. No se realizan envíos físicos.
              </p>
              <h3 className="font-playfair text-xl font-bold text-brown mt-6" data-id="element-197">
                Uso personal
              </h3>
              <p data-id="element-198">
                El contenido adquirido es para uso personal exclusivo. Queda
                prohibida la reproducción, distribución, reventa o compartición
                del material sin autorización expresa.
              </p>
              <h3 className="font-playfair text-xl font-bold text-brown mt-6" data-id="element-199">
                Garantía de satisfacción
              </h3>
              <p data-id="element-200">
                Ofrecemos una garantía de satisfacción de 7 días desde la fecha
                de compra. Si por cualquier motivo no estás satisfecho con el
                producto, podés solicitar un reembolso completo dentro de ese
                plazo.
              </p>
              <h3 className="font-playfair text-xl font-bold text-brown mt-6" data-id="element-201">
                Propiedad intelectual
              </h3>
              <p data-id="element-202">
                Todo el contenido incluido en este producto — textos, imágenes,
                técnicas y recetas — es propiedad intelectual de Empanadas de la
                Abuela y está protegido por las leyes de derechos de autor
                vigentes.
              </p>
              <h3 className="font-playfair text-xl font-bold text-brown mt-6" data-id="element-203">
                Modificaciones
              </h3>
              <p data-id="element-204">
                Nos reservamos el derecho de actualizar estos términos en
                cualquier momento. Las modificaciones entrarán en vigencia desde
                su publicación en este sitio.
              </p>
            </div>
          </div>
        </div>}
    </main>;
}